/**
 * Phase 23 (Plan 23-02) — TanStack Query hook for the bundled dashboard summary.
 *
 * Aligned with backend `Cache-Control: private, max-age=30` per D-242:
 *   - `staleTime: 30_000` matches the server cache window.
 *   - `refetchOnWindowFocus: true` re-fetches when the user returns to the tab.
 *
 * REVIEW FP-005: dropped `refetchOnMount: 'always'` — it forced a refetch on
 * every navigation back to /dashboard regardless of staleness, redundant with
 * `refetchOnWindowFocus` and the server-side `Cache-Control: max-age=30`.
 * TanStack already refetches on mount when the query is stale via `staleTime`.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type { ApiError } from '@/lib/api/error';
import { THROUGHPUT_DAYS_DEFAULT } from '@/lib/storage/throughput-window';
import { getDashboardSummaryRequest } from '@/services/dashboard';
import type { DashboardSummary } from '@/types/dashboard';

/**
 * Issue #6: `throughputDays` is part of the query key so changing the
 * throughput window re-fetches the bundled summary for that window.
 *
 * `placeholderData: keepPreviousData` keeps the previous window's summary
 * rendered (with `isPlaceholderData: true`) while the new window loads, so
 * switching windows never unmounts the dashboard — only `isLoading` on the
 * true first fetch has no data at all.
 */
export function useDashboardSummary(throughputDays: number = THROUGHPUT_DAYS_DEFAULT) {
  return useQuery<DashboardSummary, ApiError>({
    queryKey: ['dashboard-summary', throughputDays] as const,
    queryFn: () => getDashboardSummaryRequest(throughputDays),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
  });
}
